import { useEffect, useState } from "react";
import { Icon } from "@/components/Icons";
import {
  RelationshipResults,
  type RelationshipPassAction,
  type SignalRelationshipResult,
} from "@/components/RelationshipResults";
import type { FeedSignal } from "../../types";
import { useAssignableUsers, type SignalActionMutations } from "../../hooks";
import { longDate } from "../../format";
import { resolveWarmPathPractice } from "../../sliceB-core";
import {
  useCanonicalOrgId,
  useSliceBRkgModal,
  useSliceBWarmPath,
  type StrongContact,
  type WarmPath,
} from "../../sliceB";

/**
 * SURFACE 1 · Relationships at [Account].
 *
 * Replaces the redundant "Run Relationship Check" + Related-Contacts pair with a
 * single always-on module: the strong-relationship topology and the warm path
 * (from get_slice_b_surfaces rkg_modal + warm_path), plus the complementary
 * on-demand full relationship check (retained — the complete ranked list).
 *
 * R-WP-3 (owner ruling 2026-08-11): the carrier is named as the relationship
 * carrier; a practice fallback is honestly identified as a fallback; basis=none
 * renders an honest empty state — never a fabricated warm path.
 */
export function RelationshipsAtAccount({
  signal,
  viewerId,
  actions,
}: {
  signal: FeedSignal;
  viewerId: string;
  actions: SignalActionMutations;
}): JSX.Element {
  const { canonicalOrgId, status } = useCanonicalOrgId(signal);
  const warmPathPractice = resolveWarmPathPractice(signal);
  const warmPathQ = useSliceBWarmPath(canonicalOrgId, warmPathPractice);
  const rkgQ = useSliceBRkgModal(canonicalOrgId);

  const { data: assignees = [] } = useAssignableUsers();
  const rosterIds = new Set(assignees.map((a) => a.id));
  rosterIds.add(viewerId);

  // On-demand full relationship check (the complementary complete list). Persist
  // the result past the ToolButton's ~2.6s mutation reset via local state.
  const assign = actions.assign;
  const [relationships, setRelationships] = useState<SignalRelationshipResult | null>(null);
  const [passedTo, setPassedTo] = useState<string | null>(null);
  const relData = actions.relationshipCheck.data;
  useEffect(() => {
    if (relData) {
      setRelationships(relData);
      setPassedTo(null);
    }
  }, [relData]);

  const pass: RelationshipPassAction = {
    onPass: (ownerConsultantId) => {
      setPassedTo(ownerConsultantId);
      assign.mutate({
        assigneeId: ownerConsultantId,
        note: `Passed from relationship check at ${relationships?.companyName ?? signal.company}`,
      });
    },
    pendingOwnerId: assign.isPending ? passedTo : null,
    passedOwnerId: assign.isSuccess ? passedTo : null,
    errorOwnerId: assign.isError ? passedTo : null,
  };

  const warmPath = warmPathQ.data;
  const strong: StrongContact[] = rkgQ.data?.strongContacts ?? [];
  const unavailable = status === "unavailable";
  const loading = !unavailable && (warmPathQ.isPending || rkgQ.isPending);

  const runFullCheck = () => {
    actions.relationshipCheck.reset();
    actions.relationshipCheck.mutate();
  };
  const checking = actions.relationshipCheck.isPending;

  return (
    <div className="sxb-rel">
      <div className="sxb-rel-head">
        <span className="sxb-rel-title">Relationships at {signal.company}</span>
        {strong.length > 0 ? (
          <span className="sxb-rel-count">{strong.length} strong</span>
        ) : null}
      </div>
      <p className="sxb-rel-sub">
        Strong-relationship topology at this account. Run the full relationship check for the
        complete ranked list.
      </p>

      {unavailable ? (
        <p className="sxb-body-muted">
          Account relationships are not available in this view yet.
        </p>
      ) : loading ? (
        <div className="sxb-skel-block" aria-busy="true">
          <span className="sx-skel" style={{ width: "70%" }} />
          <span className="sx-skel" style={{ width: "90%" }} />
        </div>
      ) : (
        <>
          <WarmPathRow warmPath={warmPath} />

          {strong.length > 0 ? (
            <>
              <p className="sxb-rel-group-title">Strong contacts, ranked by recency</p>
              <ul className="sxb-contact-list">
                {strong.map((c) => (
                  <li key={c.contactId} className="sxb-contact">
                    <div className="sxb-contact-top">
                      <span className="sxb-contact-name">{c.name}</span>
                      <span className={`sxb-strength sxb-strength--${c.strength}`}>
                        {c.strength.toUpperCase()}
                      </span>
                    </div>
                    {c.title ? <p className="sxb-contact-title">{c.title}</p> : null}
                    <div className="sxb-contact-meta">
                      <span>Owner: {c.ownerName ?? "A colleague"}</span>
                      {c.source ? <span>{c.source}</span> : null}
                      {c.matchBasis ? <span>{c.matchBasis}</span> : null}
                      {c.interactionRecency ? (
                        <span>
                          {c.interactionCount} touches · {longDate(c.interactionRecency)}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="sxb-body-muted">
              None of your firm&rsquo;s strong relationships map to this account yet.
            </p>
          )}
        </>
      )}

      {/* Complementary on-demand full check — the complete ranked list. */}
      <div className="sxb-rel-actions">
        <button type="button" className="sx-btn sx-btn-p3" disabled={checking} onClick={runFullCheck}>
          {checking ? "Running…" : "Run full relationship check"}
        </button>
      </div>
      {relationships ? (
        <RelationshipResults
          result={relationships}
          fallbackCompany={signal.company}
          pass={pass}
          rosterIds={rosterIds}
        />
      ) : null}
      <div className="sxb-gate-note">
        <Icon name="clock" />
        <span>
          Full-depth firm-wide check (all strengths) runs on demand; introductions arrive with
          email in a later phase.
        </span>
      </div>
    </div>
  );
}

function WarmPathRow({ warmPath }: { warmPath: WarmPath | undefined }): JSX.Element {
  if (!warmPath || warmPath.basis === "none" || !warmPath.contact) {
    return (
      <div className="sxb-warm sxb-warm--none">
        <div className="sxb-warm-label">Warm path</div>
        <div className="sxb-warm-name">No warm path on record.</div>
        <div className="sxb-warm-meta">No carrier and no fallback — nothing warm is invented.</div>
      </div>
    );
  }
  if (warmPath.contact.kind === "carrier") {
    const c = warmPath.contact;
    const bits = [
      [c.authorityRole, c.authorityPractice].filter(Boolean).join(", "),
      "relationship carrier",
      `${c.relationshipContactCount} relationships here (${c.strongRelationshipCount} strong)`,
      c.lastInteractionAt ? `last touch ${longDate(c.lastInteractionAt)}` : null,
    ].filter(Boolean);
    return (
      <div className="sxb-warm">
        <div className="sxb-warm-label">Warm path</div>
        <div className="sxb-warm-name">{c.name}</div>
        <div className="sxb-warm-meta">{bits.join(" · ")}</div>
      </div>
    );
  }
  // practice_fallback — honestly identified as a fallback (R-WP-3).
  const f = warmPath.contact;
  return (
    <div className="sxb-warm sxb-warm--fallback">
      <div className="sxb-warm-label">Warm path</div>
      <div className="sxb-warm-name">{f.name}</div>
      <div className="sxb-warm-meta">
        Practice lead (fallback){f.practice ? ` · ${f.practice}` : ""} · no personal relationship on
        record
      </div>
    </div>
  );
}
